import React, { useState, useCallback } from 'react';

export interface OpenCloseSliderProps {
  value: number;                    // 0-100 (0=closed, 100=open)
  onChange: (value: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  label?: string;
  showValue?: boolean;
  colorScheme?: 'teal' | 'cyan' | 'blue';  // Color for the filled portion
}

/**
 * OpenClose Slider Control
 * 
 * 滑桿控制窗簾/捲簾開啟百分比
 * - 0% = 完全關閉
 * - 100% = 完全打開
 * 
 * MCP Command: OpenClose
 * Parameters: { openPercent: 0-100 }
 */
export const Style01_Slider: React.FC<OpenCloseSliderProps> = ({
  value,
  onChange,
  disabled = false,
  min = 0,
  max = 100,
  label = '開啟程度',
  showValue = true,
  colorScheme = 'teal',
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);

  // Sync local value with prop when not dragging
  React.useEffect(() => {
    if (!isDragging) {
      setLocalValue(value);
    }
  }, [value, isDragging]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    setLocalValue(newValue);
  }, []);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    onChange(localValue);
  }, [localValue, onChange]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    onChange(localValue);
  }, [localValue, onChange]);

  // Color scheme mapping
  const colorMap = {
    teal: {
      filled: 'from-teal-600 to-teal-400',
      thumb: 'border-teal-500',
      text: 'text-teal-400',
    },
    cyan: {
      filled: 'from-cyan-600 to-cyan-400',
      thumb: 'border-cyan-500',
      text: 'text-cyan-400',
    },
    blue: {
      filled: 'from-blue-600 to-blue-400',
      thumb: 'border-blue-500',
      text: 'text-blue-400',
    },
  };

  const colors = colorMap[colorScheme];
  const percentage = ((localValue - min) / (max - min)) * 100;

  return (
    <div className="space-y-3">
      {/* Label and Value */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-400">{label}</span>
        {showValue && (
          <span className={`text-lg font-bold ${colors.text}`}>
            {localValue}%
          </span>
        )}
      </div>

      {/* Slider */}
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          value={localValue}
          onChange={handleChange}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleTouchEnd}
          disabled={disabled}
          className={`openclose-slider w-full h-3 rounded-full cursor-pointer appearance-none
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{
            background: `linear-gradient(to right, 
              rgb(20, 184, 166) 0%, 
              rgb(45, 212, 191) ${percentage}%, 
              rgb(51, 65, 85) ${percentage}%, 
              rgb(51, 65, 85) 100%)`
          }}
        />
      </div>

      {/* Quick buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => onChange(0)}
          disabled={disabled}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors
            ${localValue === 0 
              ? 'bg-slate-600 text-white' 
              : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          關閉
        </button>
        <button
          onClick={() => onChange(50)}
          disabled={disabled}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors
            ${localValue === 50 
              ? `bg-gradient-to-r ${colors.filled} text-white` 
              : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          50%
        </button>
        <button
          onClick={() => onChange(100)}
          disabled={disabled}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors
            ${localValue === 100 
              ? `bg-gradient-to-r ${colors.filled} text-white` 
              : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          全開
        </button>
      </div>
    </div>
  );
};

export default Style01_Slider;
